
// Chakra imports
import {
  Box,
  Icon,
  SimpleGrid,
  Grid,
  useColorModeValue,
  Text,
  Flex,
  Select,
  VStack,
  Stack,
  Wrap,
  WrapItem
} from "components-lib";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useCallback } from "react";
import {
  MdPerson,
  MdLocalHospital,
  MdPeople,
  MdEventNote,
} from "react-icons/md";
import { axiosInstance, API_ENDPOINTS } from "config/api";
import ChatService from 'services/chatService';
import DoctorAlerts from 'components/alerts/DoctorAlerts';
import FrontdeskAlerts from 'components/alerts/FrontdeskAlerts';
import { HStack } from "components-lib";

// Commented out unused imports
// import MiniCalendar from "components/calendar/MiniCalendar";
// import CheckTable from "views/admin/default/components/CheckTable";
// import ComplexTable from "views/admin/default/components/ComplexTable";
// import DailyTraffic from "views/admin/default/components/DailyTraffic";
// import PieCard from "views/admin/default/components/PieCard";
// import Tasks from "views/admin/default/components/Tasks";
// import TotalSpent from "views/admin/default/components/TotalSpent";
// import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
// import {
//   columnsDataCheck,
//   columnsDataComplex,
// } from "views/admin/default/variables/columnsData";
// import tableDataCheck from "views/admin/default/variables/tableDataCheck.json";
// import tableDataComplex from "views/admin/default/variables/tableDataComplex.json";

export default function UserReports() {
  // Chakra Color Mode
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");

  // Chat participants state - simplified for new component structure
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Overview statistics state
  const [clinicStats, setClinicStats] = useState({
    total: 0,
    new: 0,
    active: 0
  });
  const [doctorStats, setDoctorStats] = useState({
    total: 0,
    new: 0,
    active: 0
  });
  const [patientStats, setPatientStats] = useState({
    total: 0,
    new: 0,
    active: 0
  });
  const [appointmentStats, setAppointmentStats] = useState({
    total: 0,
    new: 0,
    completed: 0,
    incomplete: 0,
  });

  // State for doctor-specific appointment count
  const [doctorAppointmentCount, setDoctorAppointmentCount] = useState(0);

  // Frontdesk statistics state
  const [frontdeskStats, setFrontdeskStats] = useState({
    total: 0,
    new: 0,
    active: 0
  });

  // Filter states - simplified for overview sections only
  const [selectedClinic, setSelectedClinic] = useState('all');
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [selectedFrontdesk, setSelectedFrontdesk] = useState('all');

  // Fetch clinics and doctors from API
  const [clinicOptions, setClinicOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctorOptionsRaw, setDoctorOptionsRaw] = useState([]); // raw doctor data to filter by clinic
  const [frontdeskOptions, setFrontdeskOptions] = useState([]);

  // Fetch dashboard stats helper
  const fetchDashboardStats = async (params = {}) => {
    try {
      const res = await axiosInstance.get('api/reports/dashboard-stats', { params });
      if (res.data) return res.data;
    } catch (err) {
      console.error('Error fetching reports/dashboard-stats', err?.response?.data || err.message);
    }
    return null;
  };

  // Fetch unread count for admin
  const fetchUnreadCount = useCallback(async () => {
    try {
      await ChatService.getAdminUnreadCount(1, 'admin');
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Trigger refresh for child components
  const triggerRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Doctor appointment count is handled by fetchAppointmentStats when a doctor is selected

  // Function to fetch appointment statistics from APIs
  const fetchAppointmentStats = async (doctorId = null) => {
    try {

      if (doctorId && doctorId !== 'all') {
        // Fetch doctor-specific stats
        console.log('Fetching appointment stats for doctor:', doctorId);

        // Fetch total appointments for doctor
        const totalResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.TOTAL_FOR_DOCTOR}?doctor_id=${doctorId}`);
        const totalCount = totalResponse.data?.count || 0;

        // Fetch incomplete appointments for doctor
        // const incompleteResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_PENDING}?doctor_id=${doctorId}&date=${today}`);
        const incompleteResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_PENDING}?doctor_id=${doctorId}`);
        const incompleteCount = incompleteResponse.data?.count || 0;

        // Fetch completed appointments for doctor (assuming similar endpoint structure)
        let completedCount = 0;
        try {
          // const completedResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_COMPLETED}?doctor_id=${doctorId}&date=${today}`);
          const completedResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_COMPLETED}?doctor_id=${doctorId}`);
          completedCount = completedResponse.data?.count || 0;
        } catch (error) {
          console.warn('Doctor completed endpoint not available, calculating from total and incomplete');
          completedCount = Math.max(0, totalCount - incompleteCount);
        }

        // Update appointment stats
        setAppointmentStats(prev => ({
          ...prev,
          total: totalCount,
          incomplete: incompleteCount,
          completed: completedCount,
          new: prev.new // Keep existing new count or fetch from another endpoint if available
        }));

        setDoctorAppointmentCount(totalCount);

      } else {
        // Fetch general appointment stats (existing logic)
        // This would be for when no specific doctor is selected
        console.log('Fetching general appointment stats');
        // Keep existing dashboard stats API call logic here
      }

    } catch (error) {
      console.error('Error fetching appointment stats:', error);
    }
  };

  useEffect(() => {
    // Fetch initial dashboard stats (global)
    (async () => {
      const data = await fetchDashboardStats();
      if (data) {
        setClinicStats({ total: data.total_clinics || 0, new: 0, active: data.total_clinics || 0 });
        setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
        setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
        setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
        setFrontdeskStats({ total: data.total_front_desk || 0, new: 0, active: data.total_front_desk || 0 });
      }

      // Fetch active users count (keep existing behavior)
      try {
        const res = await axiosInstance.get('/api/admin/getActiveUsers');
        if (res.data && res.data.success) {
          const { roleWiseCount, activeClinic } = res.data;
          setClinicStats(prev => ({ ...prev, active: activeClinic }));
          setDoctorStats(prev => ({ ...prev, active: roleWiseCount.doctor }));
          setPatientStats(prev => ({ ...prev, active: roleWiseCount.patient }));
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
      }

      // Fetch clinics
      try {
        const res = await axiosInstance.get('/api/clinic/allClinics');
        let clinics = res.data;
        if (Array.isArray(clinics)) {
          clinics = clinics.map(clinic => ({
            id: clinic.id || clinic._id || clinic.clinic_id,
            name: clinic.name || clinic.clinic_name
          }));
        } else if (clinics && clinics.clinics) {
          clinics = clinics.clinics.map(clinic => ({
            id: clinic.id || clinic._id || clinic.clinic_id,
            name: clinic.name || clinic.clinic_name
          }));
        }
        setClinicOptions(clinics);
      } catch (err) {
        console.error('Error fetching clinics:', err?.response?.data || err.message);
      }

      // Fetch all doctors raw so we can filter by clinic when selected
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.DOCTOR.ALL);
        if (res.data && Array.isArray(res.data)) {
          setDoctorOptionsRaw(res.data);
          const doctors = res.data.map(doctor => ({
            id: doctor.id || doctor._id,
            name: doctor.doctor || doctor.name,
            clinic_id: doctor.clinic_id || doctor.clinic || doctor.clinicId || null
          }));
          setDoctorOptions(doctors);
        }
      } catch (err) {
        console.error('Error fetching doctors:', err?.response?.data || err.message);
      }

      // Fetch unread count
      fetchUnreadCount();
    })();
  }, [fetchUnreadCount, triggerRefresh]);

  // When selectedClinic changes: filter doctors & fetch clinic-level stats & frontdesks
  useEffect(() => {
    (async () => {
      if (selectedClinic && selectedClinic !== 'all') {
        // Filter doctor options by clinic using raw data (best-effort)
        const doctors = (doctorOptionsRaw || [])
          .filter(d => {
            // normalize clinic id safely (support undefined / 0 / string / number)
            const clinicId = d?.clinic_id ?? d?.clinic ?? d?.clinicId ?? null;
            return clinicId !== null && String(clinicId) === String(selectedClinic);
          })
          .map(d => ({ id: d.id ?? d._id ?? d.doctor_id ?? null, name: d.doctor ?? d.name ?? "Unknown" }))
          .filter(Boolean);
        setDoctorOptions(doctors.length ? doctors : []);

        // Fetch frontdesk list for clinic (assumption: endpoint exists)
        try {
          const fdRes = await axiosInstance.get(`/api/frontdesk/get?clinic_id=${selectedClinic}`);
          const raw = fdRes?.data;

          // Accept several possible shapes: array, { front_desks: [...] }, { data: [...] }, { data: { front_desks: [...] } }
          let fdList = [];
          if (Array.isArray(raw)) fdList = raw;
          else if (Array.isArray(raw?.front_desks)) fdList = raw.front_desks;
          else if (Array.isArray(raw?.data)) fdList = raw.data;
          else if (Array.isArray(raw?.data?.front_desks)) fdList = raw.data.front_desks;

          // Normalize each frontdesk and ensure clinic_ids is an array
          const normalized = (fdList || [])
            .map(fd => {
              const clinic_ids = Array.isArray(fd?.clinic_ids)
                ? fd.clinic_ids
                : Array.isArray(fd?.clinicIds)
                  ? fd.clinicIds
                  : (fd?.clinic_id !== undefined && fd?.clinic_id !== null) ? [fd.clinic_id] : [];
              const id = fd?.id ?? fd?._id ?? fd?.frontdesk_id ?? fd?.front_desk_id ?? null;
              const name = `${(fd?.firstname || "").trim()} ${(fd?.lastname || "").trim()}`.trim();
              return id ? { id, name, clinic_ids } : null;
            })
            .filter(Boolean);

          const filtered = normalized.filter(fd => {
            if (!selectedClinic || selectedClinic === "all") return true;
            return (fd.clinic_ids || []).map(String).includes(String(selectedClinic));
          });

          // filtered.map(({ id, name }) => (
          //   alert(name)
          // ));

          setFrontdeskOptions(filtered);
        } catch (err) {
          console.warn('Could not fetch frontdesk list for clinic', err?.response?.data || err.message);
          setFrontdeskOptions([]);
        }

        // Fetch clinic-level dashboard stats
        const data = await fetchDashboardStats({ clinic_id: selectedClinic });
        if (data) {
          // Map response into overview sections
          setClinicStats({ total: data.total_clinics || 0, new: 0, active: data.total_clinics || 0 });
          setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
          setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
          setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
          setFrontdeskStats({ total: data.total_front_desk || 0, new: 0, active: data.total_front_desk || 0 });
        }
      } else {
        // Reset doctorOptions to all available doctors
        const doctors = doctorOptionsRaw.map(doctor => ({ id: doctor.id || doctor._id, name: doctor.doctor || doctor.name }));
        setDoctorOptions(doctors);
        setFrontdeskOptions([]);

        // Fetch global stats
        const data = await fetchDashboardStats();
        if (data) {
          setClinicStats({ total: data.total_clinics || 0, new: 0, active: data.total_clinics || 0 });
          setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
          setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
          setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
          setFrontdeskStats({ total: data.total_front_desk || 0, new: 0, active: data.total_front_desk || 0 });
        }
      }
    })();
  }, [selectedClinic, doctorOptionsRaw]);

  // When selectedDoctor or selectedFrontdesk change: fetch appropriate dashboard stats
  useEffect(() => {
    const interval = setInterval(() => {
      triggerRefresh();
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [triggerRefresh]);
  //           setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
  //           setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
  //         }
  //       } else if (selectedDoctor && selectedDoctor !== 'all') {
  //         // Fetch doctor-specific (and clinic if selected) stats
  //         const params = {};
  //         if (selectedClinic && selectedClinic !== 'all') params.clinic_id = selectedClinic;
  //         params.doctor_id = selectedDoctor;
  //         const data = await fetchDashboardStats(params);
  //         if (data) {
  //           // Per request: show this data in Patient Overview
  //           setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
  //           // Per request: set doctor stats as Total 1 new 0 active 1 (explicit requirement)
  //           setDoctorStats({ total: 1, new: 0, active: 1 });
  //           // Keep clinic stats as-is
  //           setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
  //         }
  //       } else {
  //         // No specific doctor/frontdesk selected - if a clinic is selected, clinic-level was handled by other effect
  //         if (!selectedClinic || selectedClinic === 'all') {
  //           const data = await fetchDashboardStats();
  //           if (data) {
  //             setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
  //             setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
  //             setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
  //             setFrontdeskStats({ total: data.total_front_desk || 0, new: 0, active: data.total_front_desk || 0 });
  //           }
  //         }
  //       }
  //     } catch (err) {
  //       console.error('Error updating stats for selection change', err?.response?.data || err.message);
  //     }
  //   })();
  // }, [selectedDoctor, selectedFrontdesk, selectedClinic]);



  // Fetch doctor-specific appointment count when doctor is selected
  useEffect(() => {
    if (selectedDoctor !== 'all' && selectedDoctor) {
      fetchAppointmentStats(selectedDoctor);
    } else {
      setDoctorAppointmentCount(0);
      // Reset to default appointment stats when no doctor is selected
      setAppointmentStats({
        total: 0,
        new: 0,
        completed: 0,
        incomplete: 0
      });
    }
  }, [selectedDoctor]);

  // NOTE: periodic refresh handled by the single effect above (5 minutes).
  // Removed duplicate interval registration to avoid duplicated polling.



  // Update overview statistics when filters change
  // Removed hardcoded demo stats. All overview data is driven by API calls

  // Fetch doctor-specific appointment count when doctor is selected
  useEffect(() => {
    if (selectedDoctor !== 'all' && selectedDoctor) {
      // Fetch appointment numbers for the selected doctor only
      fetchAppointmentStats(selectedDoctor);
    } else {
      // No specific doctor selected: reset doctor-specific counters and show clinic/global stats
      setDoctorAppointmentCount(0);

      (async () => {
        try {
          if (selectedClinic && selectedClinic !== 'all') {
            // Fetch clinic-level stats
            const data = await fetchDashboardStats({ clinic_id: selectedClinic });
            if (data) {
              setClinicStats({ total: data.total_clinics || 0, new: 0, active: data.total_clinics || 0 });
              setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
              setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
              setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
              setFrontdeskStats({ total: data.total_front_desk || 0, new: 0, active: data.total_front_desk || 0 });
            }
          } else {
            // No clinic and no doctor selected: fetch global stats
            const data = await fetchDashboardStats();
            if (data) {
              setClinicStats({ total: data.total_clinics || 0, new: 0, active: data.total_clinics || 0 });
              setDoctorStats({ total: data.total_doctors || 0, new: 0, active: data.total_doctors || 0 });
              setPatientStats({ total: data.total_patients || 0, new: 0, active: data.total_patients || 0 });
              setAppointmentStats({ total: data.total_appointments || 0, new: 0, completed: 0, incomplete: 0 });
              setFrontdeskStats({ total: data.total_front_desk || 0, new: 0, active: data.total_front_desk || 0 });
            }
          }
        } catch (err) {
          console.error('Error fetching stats when doctor deselected', err?.response?.data || err.message);
        }
      })();
    }
  }, [selectedDoctor, selectedClinic]);

  // history is not used in this view



  const sidebarBg = useColorModeValue("white", "navy.900");
  const sidebarSectionBg = useColorModeValue("secondaryGray.100", "navy.850");
  const mainCardBg = useColorModeValue("whiteAlpha.900", "navy.800");
  const sectionShadow = useColorModeValue("0 20px 45px rgba(15, 23, 42, 0.15)", "0 30px 60px rgba(2, 12, 27, 0.8)");

  return (
    <Box bg="blue.50" tabIndex={-1} mt="35px" minH="100vh" className="pt-25" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <Box w="100%"  mx="auto" display="flex" flexDirection="column" gap={6}>
        <Grid templateColumns="repeat(auto-fit, minmax(320px, 1fr))" gap={6}>
          <Box
            w="100%"
            h="100%"
            bg={sidebarBg}
            borderRadius="30px"
            p={{ base: 5, md: 6 }}
            border="1px solid"
            borderColor={useColorModeValue("gray.100", "whiteAlpha.200")}
            boxShadow={sectionShadow}
          >
            <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "flex-start", sm: "flex-end" }} mb={4} gap={4}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor}>
                Clinics
              </Text>
              <Select
                value={selectedClinic}
                onChange={(e) => setSelectedClinic(e.target.value)}
                w={{ base: "100%", sm: "180px" }}
                size="sm"
                borderRadius="lg"
              >
                <option value="all">All Clinics</option>
                {clinicOptions.map(clinic => (
                  <option key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </option>
                ))}
              </Select>
            </Stack>
            <VStack spacing={3}>
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdLocalHospital} color={brandColor} />}
                  />
                }
                name="Total Clinics"
                value={clinicStats.total.toString()}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdLocalHospital} color={brandColor} />}
                  />
                }
                name="New Clinics"
                value={clinicStats.new.toString()}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdLocalHospital} color={brandColor} />}
                  />
                }
                name="Active Clinics"
                value={clinicStats.active.toString()}
              />
            </VStack>
          </Box>

          <Box
            w="100%"
            h="100%"
            bg={sidebarBg}
            borderRadius="30px"
            p={{ base: 5, md: 6 }}
            border="1px solid"
            borderColor={useColorModeValue("gray.100", "whiteAlpha.200")}
            boxShadow={sectionShadow}
          >
            <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "flex-start", sm: "flex-end" }} mb={4} gap={4}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor}>
                Doctors
              </Text>
              <Select
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
                w={{ base: "100%", sm: "180px" }}
                size="sm"
                borderRadius="lg"
              >
                <option value="all">All Doctors</option>
                {doctorOptions.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </option>
                ))}
              </Select>
            </Stack>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdPerson} color={brandColor} />}
                  />
                }
                name="Total Doctors"
                value={doctorStats.total.toString()}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdPerson} color={brandColor} />}
                  />
                }
                name="New Doctors"
                value={doctorStats.new.toString()}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdPerson} color={brandColor} />}
                  />
                }
                name="Active Doctors"
                value={doctorStats.active.toString()}
              />
            </SimpleGrid>
          </Box>

          <Box
            w="100%"
            h="100%"
            bg={sidebarBg}
            borderRadius="30px"
            p={{ base: 5, md: 6 }}
            border="1px solid"
            borderColor={useColorModeValue("gray.100", "whiteAlpha.200")}
            boxShadow={sectionShadow}
          >
            <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "flex-start", sm: "flex-end" }} mb={4} gap={4}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor}>
                Frontdesk
              </Text>
              <Select
                value={selectedFrontdesk}
                onChange={(e) => setSelectedFrontdesk(e.target.value)}
                w={{ base: "100%", sm: "180px" }}
                size="sm"
                borderRadius="lg"
              >
                <option value="all">All Frontdesk</option>
                {frontdeskOptions.map(fd => (
                  <option key={fd.id} value={fd.id}>{fd.name}</option>
                ))}
              </Select>
            </Stack>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdPerson} color={brandColor} />}
                  />
                }
                name="Total Frontdesk"
                value={frontdeskStats.total.toString()}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdPerson} color={brandColor} />}
                  />
                }
                name="New Frontdesk"
                value={frontdeskStats.new.toString()}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdPerson} color={brandColor} />}
                  />
                }
                name="Active Frontdesk"
                value={frontdeskStats.active.toString()}
              />
            </SimpleGrid>
          </Box>
        </Grid>

        {/* // Appointments & Patients Overview Section */}
        <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6}>
          {/* Appointments */}
          <Box
            h="100%"
            w="100%"
            bg={mainCardBg}
            borderRadius="30px"
            p={{ base: 5, md: 7 }}
            boxShadow={sectionShadow}
            border="1px solid"
            borderColor={useColorModeValue("gray.100", "whiteAlpha.800")}
          >
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor}>
                Appointments Overview
              </Text>
              <Text fontSize="sm" color={textColorSecondary}>
                Live data updates every five minutes
              </Text>
            </Flex>
            <Wrap spacing={4} justify="flex-start">
              <WrapItem flex="1" minW="140px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="40px"
                      h="40px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdEventNote} color={brandColor} />}
                    />
                  }
                  name="Total Appointments"
                  value={selectedDoctor !== 'all' ? doctorAppointmentCount.toString() : appointmentStats.total.toString()}
                />
              </WrapItem>
              <WrapItem flex="1" minW="140px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="40px"
                      h="40px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdEventNote} color={brandColor} />}
                    />
                  }
                  name="New Appointments"
                  value={appointmentStats.new.toString()}
                />
              </WrapItem>
              <WrapItem flex="1" minW="140px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="40px"
                      h="40px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdEventNote} color={brandColor} />}
                    />
                  }
                  name="Completed"
                  value={appointmentStats.completed.toString()}
                />
              </WrapItem>
              <WrapItem flex="1" minW="140px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="40px"
                      h="40px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdEventNote} color={brandColor} />}
                    />
                  }
                  name="Incomplete"
                  value={appointmentStats.incomplete.toString()}
                />
              </WrapItem>
            </Wrap>
          </Box>
          {/* Patients */}
          <Box
            h="100%"
            w="100%"
            bg={mainCardBg}
            borderRadius="30px"
            p={{ base: 5, md: 7 }}
            border="1px solid"
            borderColor={useColorModeValue("gray.100", "whiteAlpha.800")}
            boxShadow={sectionShadow}
          >
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor} mb={4}>
              Patients Overview
            </Text>
            <Wrap spacing={4} justify="flex-start">
              <WrapItem flex="1" minW="150px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="48px"
                      h="48px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdPeople} color={brandColor} />}
                    />
                  }
                  name="Total Patients"
                  value={patientStats.total.toString()}
                />
              </WrapItem>
              <WrapItem flex="1" minW="150px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="48px"
                      h="48px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdPeople} color={brandColor} />}
                    />
                  }
                  name="New Patients"
                  value={patientStats.new.toString()}
                />
              </WrapItem>
              <WrapItem flex="1" minW="150px">
                <MiniStatistics
                  startContent={
                    <IconBox
                      w="48px"
                      h="48px"
                      bg={boxBg}
                      icon={<Icon w="28px" h="28px" as={MdPeople} color={brandColor} />}
                    />
                  }
                  name="Active Patients"
                  value={patientStats.active.toString()}
                />
              </WrapItem>
            </Wrap>
          </Box>
        </Grid>
        

        {/* // Alerts Section */}
        <Box
          bg={mainCardBg}
          borderRadius="30px"
          p={{ base: 5, md: 7 }}
          border="1px solid"
          borderColor={useColorModeValue("gray.100", "whiteAlpha.200")}
          boxShadow={sectionShadow}
        >
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor} mb={4}>
            Alerts
          </Text>
          <Grid templateColumns="repeat(auto-fit, minmax(320px, 1fr))" gap={5}>
            <Box
              bg={sidebarSectionBg}
              borderRadius="22px"
              p={2}
              w="100%"
              h="100%"
              border="1px solid"
              borderColor={useColorModeValue("gray.100", "whiteAlpha.200")}
            >
              <DoctorAlerts
                clinicOptions={clinicOptions}
                doctorOptions={doctorOptions}
                doctorOptionsRaw={doctorOptionsRaw}
                selectedClinic={selectedClinic}
                onClinicChange={setSelectedClinic}
                refreshTrigger={refreshTrigger}
              />
            </Box>
            <Box
              bg={sidebarSectionBg}
              borderRadius="22px"
              w="100%"
              h="100%"
              p={2}
              border="1px solid"
              borderColor={useColorModeValue("gray.100", "whiteAlpha.200")}
            >
              <FrontdeskAlerts
                h="100%"
                clinicOptions={clinicOptions}
                frontdeskOptions={frontdeskOptions}
                selectedClinic={selectedClinic}
                onClinicChange={setSelectedClinic}
                refreshTrigger={refreshTrigger}
              />
            </Box>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
